import React, {
  useMemo, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useClickOutside } from '../../hooks';
import SearchChipsCarousel from './SearchChipsCarousel';
import Icon from '../Icons/Icon';
import Button from '../Button/Button';
import tailwindTheme from '../../../../../config/tailwind.config';
import CustomSelect from '../FormInputs/CustomSelect/CustomSelect';
import Number from '../FormInputs/Number/Number';
import AutocompleteText from '../FormInputs/AutocompleteText/AutocompleteText';
import DateSelectorV2 from '../FormInputs/DateSelectorV2/DateSelectorV2';
import TextInput from '../FormInputs/TextInput/TextInput';

const DEFAULT_CHIPS_SETTINGS = {};
const DEFAULT_DATE_ICON_STYLES = { bottom: 0, right: 0, marginBottom: 0 };
const DEFAULT_ACTIVE_SEARCH_PARAMS = {};

const Search = ({
  title = 'Search', fields = [], chipsSettings = DEFAULT_CHIPS_SETTINGS, onSearch = () => { }, dateIconStyles = DEFAULT_DATE_ICON_STYLES, activeSearchParams = DEFAULT_ACTIVE_SEARCH_PARAMS,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useClickOutside(open, () => setOpen(false));
  const defaultOptions = {};
  fields.forEach(value => { if (value.type === 'autocompleteText') defaultOptions[value.name] = ''; });
  const [selectedOptions, setSelectedOptions] = useState(defaultOptions);
  const defaultValues = {};
  fields.forEach(value => { defaultValues[value.name] = ''; });
  const [values, setValues] = useState(defaultValues);

  useEffect(() => {
    setValues(activeSearchParams);
  }, [activeSearchParams]);

  const chips = useMemo(() => {
    return Object.entries(activeSearchParams).filter(([, value]) => value).map(([key, value]) => {
      return { key, value };
    });
  }, [activeSearchParams]);
  const getOnChange = (fieldName) => {
    return (e) => {
      setValues({ ...values, [fieldName]: e.target.value });
    };
  };
  const onClickCloseChip = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const updated = { ...activeSearchParams };
    updated[key] = '';
    onSearch(updated);
    setValues(prev => ({ ...prev, [key]: '' }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    onSearch(values);
    setOpen(false);
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: tailwindTheme.theme.extend.colors['vamos-blue-gray'],
      borderRadius: '0.375rem',
      fontFamily: 'DM Sans',
      fontWeight: '400',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: tailwindTheme.theme.extend.colors['vamos-black'],
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: tailwindTheme.theme.extend.colors['vamos-black'],
    }),
  };

  // renders for search block when additional menu is NOT expanded
  if (!open) {
    if (chips.length > 0) {
      // renders if closed and chips are present
      return (
        <div className="flex gap-x-1.5 relative inline-block text-left border rounded-md bg-vamos-true-white px-3 py-2 text-sm max-w-[702px] min-w-[240px] h-11.5">
          <SearchChipsCarousel chips={chips} onClickCloseChip={onClickCloseChip} chipsSettings={chipsSettings} />
          <button
            type="button"
            aria-label="expand search"
            onClick={() => { setOpen(!open); }}
            onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)}
            data-testid="expand-search-button"
          >
            <Icon name="search" />
          </button>
        </div>
      );
    }
    // renders if no chips
    return (
      <div className="relative text-left max-w-[240px] min-w-[240px] h-11.5">
        <button
          type="button"
          aria-label="expand search"
          className="flex h-full items-center justify-between w-full justify-center gap-x-1.5 border rounded-md bg-vamos-true-white px-3 py-2 text-sm text-vamos-gray"
          onClick={() => { setOpen(!open); }}
          onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)}
          data-testid="expand-search-button"
        >
          {title}
          <Icon name="search" />
        </button>

      </div>
    );
  }

  // renders for search block when additional menu is expanded
  return (
    <div ref={containerRef} className="relative inline-block text-left max-w-[240px] min-w-[240px] h-11.5 ">
      <div className="absolute w-full top-0 left-0 border rounded-md bg-vamos-true-white text-sm z-10">
        <div className="relative text-left max-w-[240px] min-w-[240px] h-11.5">
          <button
            type="button"
            aria-label="collapse search"
            className="flex h-full items-center justify-between w-full justify-center gap-x-1.5 px-3 py-2 text-sm text-vamos-gray"
            onClick={() => { setOpen(!open); }}
            onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)}
          >
            {title}
            <Icon name="expanded" />
          </button>

        </div>

        <form className="px-2 py-2 space-y-3 w-full pl-4 pr-4" onSubmit={onSubmit}>
          {fields.map((field) => {
            if (field.type === 'text') {
              return (
                <TextInput
                  label={field.label}
                  value={values[field.name]}
                  setValue={v => setValues(prev => ({ ...prev, [field.name]: v }))}
                  shouldFocus={fields[0]?.name === field.name}
                  dataTestId={`${field.name}-input`}
                />
              );
            }
            if (field.type === 'autocompleteText') {
              return (
                <AutocompleteText
                  label={field.label}
                  value={selectedOptions[field.name]}
                  onChange={option => {
                    setSelectedOptions(prev => ({ ...prev, [field.name]: option }));
                    setValues(prev => ({ ...prev, [field.name]: option?.value ?? '' }));
                  }}
                  options={field.options}
                />
              );
            }
            if (field.type === 'dropdown') {
              const getOptions = (array) => array?.map(item => ({
                label: item, value: item, dataTestId: `option-${String(item).toLowerCase()}`,
              }));
              const fieldOptions = getOptions(field.options);
              return (
                <CustomSelect
                  key={field.name}
                  name={field.label}
                  label={field.label}
                  value={values[field.name]}
                  setValue={(opt) => setValues((prev) => ({ ...prev, [field.name]: opt?.value ?? '' }))}
                  placeholder={field.dropdownPlaceholder}
                  shouldShowClearOption={field.shouldShowClearOption}
                  clearOptionLabel={field.clearOptionLabel}
                  options={fieldOptions}
                  styles={customStyles}
                  dataTestId={`${field.name}-dropdown`}
                />
              );
            }
            if (field.type === 'number') {
              return (
                <Number
                  label={field.label}
                  value={values[field.name]}
                  setValue={v => setValues(prev => ({ ...prev, [field.name]: v }))}
                  dataTestId={`${field.name}-input`}
                />
              );
            }
            if (field.type === 'date') {
              return (
                <DateSelectorV2
                  label={field.label}
                  name={field.label}
                  value={values[field.name]}
                  setValue={v => setValues(prev => ({ ...prev, [field.name]: v }))}
                  shouldDefaultDate={false}
                  dateIconStyles={dateIconStyles}
                  dataTestId={`${field.name}-input`}
                />
              );
            }
            return (
              <div key={field.name}>
                <label htmlFor={field.name} className="block text-sm mb-0">{field.label}</label>
                <input
                  type={field.type}
                  name={field.name}
                  value={values[field.name]}
                  className="block w-full px-1 py-1 rounded-md mt-0 bg-vamos-white border border-vamos-blue-gray"
                  onChange={getOnChange(field.name)}
                />
              </div>
            );
          })}
          <div className="flex justify-end pt-4 pb-3">
            <Button label="Search" size="md" type="submit" dataTestId="search-submit" />
          </div>
        </form>

      </div>

    </div>
  );
};

// Prop types for component props validation
Search.propTypes = {
  title: PropTypes.string, // PropType for title
  onSearch: PropTypes.func, // PropType for onSearch, marking as required
  fields: PropTypes.arrayOf(PropTypes.shape({ // PropType for fields
    label: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
  })),
  chipsSettings: PropTypes.objectOf(PropTypes.shape({ // PropType for chipsSettings
    prefix: PropTypes.string,
    type: PropTypes.string,
  })),
  dateIconStyles: PropTypes.object,
  activeSearchParams: PropTypes.object,
};

export default Search;
